import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { PaginationMeta } from '@/lib/api/schemas';
import { EmptyState } from './EmptyState';
import { Pagination } from './Pagination';
import { Skeleton } from './Skeleton';

export interface Column<T> {
  /** Unique key. Also the sort key unless `sortValue` is given. */
  key: string;
  header: ReactNode;
  /** Cell renderer; defaults to `String(row[key])`. */
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  /** Value used for client-side sorting (default `row[key]`). */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  /** Tailwind classes for the <th>/<td> (e.g. 'w-32', 'hidden md:table-cell'). */
  className?: string;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[] | undefined;
  rowKey: (row: T) => string | number;
  /** Accessible name of the table. REQUIRED. */
  caption: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Shown when `data` is empty. */
  empty?: ReactNode;
  /** Rows are focusable/clickable when provided (Enter/Space activate). */
  onRowClick?: (row: T) => void;
  /**
   * Sorting: uncontrolled client-side sort of the CURRENT PAGE by default.
   * Pass `sort` + `onSortChange` for controlled (e.g. when the endpoint supports `sort` params).
   */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** API pagination meta; renders <Pagination/> under the table. */
  meta?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  /** Dense rows (admin screens). Default true; POS-ish screens pass false for roomier rows. */
  dense?: boolean;
}

const alignCls = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

/**
 * Generic table: sortable headers (aria-sort), loading skeleton, error + retry, empty state,
 * API-shaped pagination. Dense by default.
 */
export function DataTable<T>({
  columns,
  data,
  rowKey,
  caption,
  loading,
  error,
  onRetry,
  empty,
  onRowClick,
  sort: controlledSort,
  onSortChange,
  meta,
  onPageChange,
  onPerPageChange,
  dense = true,
}: DataTableProps<T>) {
  const [localSort, setLocalSort] = useState<SortState | null>(null);
  const controlled = onSortChange !== undefined;
  const sort = controlled ? (controlledSort ?? null) : localSort;

  const rows = useMemo(() => {
    if (!data) return [];
    if (controlled || !sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return data;
    const get = (r: T) =>
      col.sortValue
        ? col.sortValue(r)
        : ((r as Record<string, unknown>)[col.key] as string | number | null);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return (
        String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) *
        dir
      );
    });
  }, [data, sort, columns, controlled]);

  const toggleSort = (key: string) => {
    const next: SortState | null =
      sort?.key !== key ? { key, dir: 'asc' } : sort.dir === 'asc' ? { key, dir: 'desc' } : null;
    if (controlled) onSortChange?.(next);
    else setLocalSort(next);
  };

  const cellPad = dense ? 'px-3 py-2' : 'px-4 py-3.5';

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="relative overflow-x-auto">
        <table className="w-full border-collapse text-sm" aria-busy={loading || undefined}>
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-surface-2 text-xs tracking-wide text-fg-muted uppercase">
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={
                      active
                        ? sort!.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : c.sortable
                          ? 'none'
                          : undefined
                    }
                    className={cn(
                      'font-semibold',
                      cellPad,
                      alignCls[c.align ?? 'left'],
                      c.className,
                    )}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded uppercase hover:text-fg',
                          c.align === 'right' && 'flex-row-reverse',
                          active && 'text-fg',
                        )}
                      >
                        {c.header}
                        {active ? (
                          sort!.dir === 'asc' ? (
                            <ArrowUp aria-hidden className="size-3.5" />
                          ) : (
                            <ArrowDown aria-hidden className="size-3.5" />
                          )
                        ) : (
                          <ArrowUpDown aria-hidden className="size-3.5 opacity-50" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && !data
              ? Array.from({ length: 6 }, (_, i) => (
                  <tr key={`sk-${i}`}>
                    {columns.map((c) => (
                      <td key={c.key} className={cellPad}>
                        <Skeleton className="h-4 w-full max-w-40" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (
                              e.target === e.currentTarget &&
                              (e.key === 'Enter' || e.key === ' ')
                            ) {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      'hover:bg-surface-2',
                      onRowClick && 'cursor-pointer focus-visible:bg-surface-2',
                      loading && 'opacity-60',
                    )}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={cn(cellPad, alignCls[c.align ?? 'left'], c.className)}
                      >
                        {c.cell
                          ? c.cell(row)
                          : String((row as Record<string, unknown>)[c.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {error && !loading && (
        <div role="alert" className="flex flex-col items-center gap-2 px-6 py-10 text-center">
          <p className="font-medium text-danger">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-sm font-medium text-primary underline"
            >
              Try again
            </button>
          )}
        </div>
      )}
      {!error &&
        !loading &&
        data &&
        data.length === 0 &&
        (empty ?? <EmptyState title="Nothing to show" />)}

      {meta && onPageChange && !error && (
        <Pagination meta={meta} onPageChange={onPageChange} onPerPageChange={onPerPageChange} />
      )}
    </div>
  );
}
